import React, { useState, useEffect, useContext, useMemo } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import Select from "react-select";
import debounce from "lodash.debounce";
import UserContext from "../../context/users/userContext";
import CustomersContext from "../../context/otogas/customers/customersContext";

/**
 * Props:
 *  - show, handleClose
 *  - submitting?: boolean   // controls Save button spinner
 *  - handleSave(payload)
 */
const PriceApprovalModal = ({ show, handleClose, handleSave, submitting = false }) => {
  const { users, fetchUsers } = useContext(UserContext);
  const { vehicles, fetchVehicles } = useContext(CustomersContext);

  const [form, setForm] = useState({
    quantity: 0,
    originalPrice: 0,
    proposedPrice: 0,
    numberPlate: "",
    approverUserCode: "",
    notes: "",
    shiftNumber: "",
  });
  const [errors, setErrors] = useState({});

  // Vehicle select state
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const [vehicleError, setVehicleError] = useState("");
  const [vLoading, setVLoading] = useState(false);

  // Users -> react-select options (string value)
  const userOptions = useMemo(() => {
    const list = Array.isArray(users)
      ? users
      : Array.isArray(users?.data) ? users.data
      : Array.isArray(users?.responseObject) ? users.responseObject
      : Array.isArray(users?.responseObject?.data) ? users.responseObject.data
      : [];
    return list
      .map((u) => ({
        value: String(u.userCode ?? u.id ?? u.code ?? ""),
        label:
          [u.firstName, u.lastName].filter(Boolean).join(" ") ||
          u.name || u.userName || u.email || "Unknown",
      }))
      .filter((o) => o.value);
  }, [users]);

  const selectedApprover = useMemo(
    () => userOptions.find((o) => o.value === form.approverUserCode) || null,
    [userOptions, form.approverUserCode]
  );

  // Open: load users
  useEffect(() => {
    if (!show) return;
    setErrors({});
    fetchUsers && fetchUsers();
  }, [show, fetchUsers]);

  // Debounced vehicle search
  const debouncedSearch = useMemo(
    () =>
      debounce((reg) => {
        setVLoading(true);
        fetchVehicles?.(1, 10, "", reg, "", "", true);
      }, 400),
    [fetchVehicles]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    if (!vehicles) return;
    const opts = vehicles.map((v) => ({
      value: v.vehicleCode,
      label: v.vehicleRegistrationNumber,
    }));
    setVehicleOptions(opts);
    setVLoading(false);
  }, [vehicles]);

  const onChange = (e) => {
    const { name, value, type } = e.target;
    setForm((p) => ({
      ...p,
      [name]: type === "number" ? (value === "" ? "" : Number(value)) : value,
    }));
  };

  const onVehicleChange = (opt) => {
    if (!opt) {
      setVehicleDetails(null);
      setForm((p) => ({ ...p, numberPlate: "" }));
      return;
    }
    setVehicleDetails({ vehicleCode: opt.value, vehicleRegistrationNumber: opt.label });
    setForm((p) => ({ ...p, numberPlate: opt.label.toUpperCase() }));
    setVehicleError("");
  };

  const onApproverChange = (opt) =>
    setForm((p) => ({ ...p, approverUserCode: opt ? String(opt.value) : "" }));

  const validate = () => {
    const v = {};
    if (form.quantity === "" || Number(form.quantity) < 0) v.quantity = "Enter a valid quantity";
    if (form.originalPrice === "" || Number(form.originalPrice) < 0) v.originalPrice = "Enter a valid amount";
    if (form.proposedPrice === "" || Number(form.proposedPrice) < 0) v.proposedPrice = "Enter a valid amount";
    if (!form.numberPlate?.trim()) v.numberPlate = "Required (select a vehicle)";
    if (!String(form.approverUserCode).trim()) v.approverUserCode = "Required";
    if (!form.shiftNumber?.trim()) v.shiftNumber = "Required";
    return v;
  };

  const onSubmit = () => {
    const v = validate();
    if (Object.keys(v).length) {
      setErrors(v);
      if (v.numberPlate) setVehicleError(v.numberPlate);
      return;
    }
    const payload = {
      quantity: Number(form.quantity),
      originalPrice: Number(form.originalPrice),
      proposedPrice: Number(form.proposedPrice),
      numberPlate: form.numberPlate.trim().toUpperCase(),
      approverUserCode: String(form.approverUserCode).trim(),
      notes: form.notes?.trim() ?? "",
      shiftNumber: form.shiftNumber.trim(),
    };
    handleSave?.(payload);
  };

  const disableSave = Object.keys(validate()).length > 0 || submitting;

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title>Price Change Approval</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group controlId="quantity" className="mb-3">
            <Form.Label>Quantity</Form.Label>
            <Form.Control
              type="number" min="0" step="1"
              name="quantity" value={form.quantity} onChange={onChange}
              isInvalid={!!errors.quantity}
            />
            <Form.Control.Feedback type="invalid">{errors.quantity}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="originalPrice" className="mb-3">
            <Form.Label>Original Price</Form.Label>
            <Form.Control
              type="number" min="0" step="0.01"
              name="originalPrice" value={form.originalPrice} onChange={onChange}
              isInvalid={!!errors.originalPrice}
            />
            <Form.Control.Feedback type="invalid">{errors.originalPrice}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="proposedPrice" className="mb-3">
            <Form.Label>Proposed Price</Form.Label>
            <Form.Control
              type="number" min="0" step="0.01"
              name="proposedPrice" value={form.proposedPrice} onChange={onChange}
              isInvalid={!!errors.proposedPrice}
            />
            <Form.Control.Feedback type="invalid">{errors.proposedPrice}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="vehicleRegistration" className="mb-3">
            <Form.Label>Vehicle Registration</Form.Label>
            <Select
              options={vehicleOptions}
              onChange={onVehicleChange}
              onInputChange={(input) => {
                if ((input || "").length >= 2) debouncedSearch(input);
              }}
              placeholder="Search & select vehicle…"
              value={vehicleOptions.find((o) => o.value === vehicleDetails?.vehicleCode) || null}
              isClearable
            />
            {vehicleError && <Form.Text className="text-danger">{vehicleError}</Form.Text>}
            {vLoading && <div className="mt-1"><Spinner animation="border" size="sm" /></div>}
          </Form.Group>

          <Form.Group controlId="approverUserCode" className="mb-3">
            <Form.Label>Approver</Form.Label>
            <Select
              options={userOptions}
              value={selectedApprover}
              onChange={onApproverChange}
              placeholder="Select approver (First Last)…"
              isClearable
            />
            {errors.approverUserCode && (
              <div className="invalid-feedback d-block">{errors.approverUserCode}</div>
            )}
          </Form.Group>

          <Form.Group controlId="shiftNumber" className="mb-3">
            <Form.Label>Shift Number</Form.Label>
            <Form.Control
              type="text" name="shiftNumber" value={form.shiftNumber} onChange={onChange}
              placeholder="Enter shift number" isInvalid={!!errors.shiftNumber}
            />
            <Form.Control.Feedback type="invalid">{errors.shiftNumber}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="notes" className="mb-0">
            <Form.Label>Notes (optional)</Form.Label>
            <Form.Control
              as="textarea" rows={2}
              name="notes" value={form.notes} onChange={onChange}
              placeholder="Add any remarks…"
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onSubmit} disabled={disableSave}>
          {submitting ? (
            <>
              <Spinner as="span" size="sm" animation="border" className="me-2" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PriceApprovalModal;
